import { NodeVariants } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import { Edge, Node } from 'reactflow';

// Die Umrechnung Tage/Stunden/Minuten lebt in `time-parts.utils` (ohne Abhängigkeiten, damit der Wizard sie ebenfalls nutzt und testet).
export { convertToSeconds, splitTimeInSeconds } from './time-parts.utils';

/**
 * Helper function to find all downstream nodes connected to a given node.
 * @param {string} nodeId - ID of the starting node.
 * @param {Edge[]} edges - Current edges in the flow.
 * @returns {string[]} Array of connected node IDs.
 */
export const findConnectedNodes = (nodeId: string, edges: Edge[]): string[] => {
  const visited = new Set<string>();
  const stack = [nodeId];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current && !visited.has(current)) {
      visited.add(current);
      const downstreamNodes = edges.filter((edge) => edge.source === current).map((edge) => edge.target);

      stack.push(...downstreamNodes);
    }
  }

  return Array.from(visited);
};

export const resolveOverlaps = (nodes: Node[]): Node[] => {
  const updatedNodes = [...nodes];
  const padding = 20;

  for (let i = 0; i < updatedNodes.length; i++) {
    for (let j = i + 1; j < updatedNodes.length; j++) {
      const node1 = updatedNodes[i];
      const node2 = updatedNodes[j];

      const isTooClose = Math.abs(node1.position.x - node2.position.x) < padding && Math.abs(node1.position.y - node2.position.y) < padding;

      if (isTooClose) {
        updatedNodes[j] = {
          ...node2,
          position: {
            x: node2.position.x + padding,
            y: node2.position.y + padding,
          },
        };
      }
    }
  }

  return updatedNodes;
};

const getStartingNodes = (nodes: Node[], edges: Edge[]) => {
  const targetNodes = edges.map((edge) => edge.target);
  return nodes.filter((node) => !targetNodes.includes(node.id));
};

export const numberFunctionNodes = (nodes: Node[], edges: Edge[]): Node[] => {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const adjacencyList = new Map<string, string[]>();

  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
  });

  edges.forEach(({ source, target, sourceHandle }) => {
    if (!adjacencyList.has(source)) return;
    if (sourceHandle === 'success') {
      adjacencyList.get(source)!.unshift(target);
    } else {
      adjacencyList.get(source)!.push(target);
    }
  });

  const visited = new Set<string>();
  let count = 1;

  const traverse = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (node && node.type === 'functionNode') {
      node.data.functionNumber = count++;
    }

    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      traverse(neighbor);
    }
  };

  const startNodes = getStartingNodes(nodes, edges);
  startNodes.forEach((startNode) => traverse(startNode.id));

  return nodes;
};

const NEW_NODE_HEIGHTS: Record<string, number> = {
  conditionalNode: 141 + 48,
  waitNode: 172,
  functionNode: 141,
  conditionWithDurationNode: 264 + 48,
  utilityNode: 141,
};

export const migrateOldToNewFlowStructure = (nodes: Node[], edges: Edge[]): Node[] => {
  const nodeMap = new Map(nodes.map((node) => [node.id, { ...node }]));

  // Create adjacency list to track children
  const childMap = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!childMap.has(edge.source)) {
      childMap.set(edge.source, []);
    }
    childMap.get(edge.source)!.push(edge.target);
  });

  // Sort nodes by their initial Y position (process top-down)
  const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);

  for (const parentNode of sortedNodes) {
    const children = childMap.get(parentNode.id) || [];
    const parentHeight = NEW_NODE_HEIGHTS[parentNode.type!] || 100;
    let currentY = parentNode.position.y + parentHeight + 84;

    children.forEach((childId) => {
      if (nodeMap.has(childId)) {
        const childNode = nodeMap.get(childId)!;
        const childHeight = NEW_NODE_HEIGHTS[childNode.type!] || 100;

        // Update Y position
        childNode.position.y = currentY;
        childNode.positionAbsolute = { ...childNode.position };

        // Update dimensions
        childNode.width = 300;
        childNode.height = childHeight;

        // Move down for the next child
        currentY += childHeight + 84;
      }
    });
  }

  return Array.from(nodeMap.values());
};

const OLD_NODE_HEIGHTS: Record<string, number> = {
  conditionalNode: 98,
  waitNode: 60,
  functionNode: 60,
  conditionWithDurationNode: 161,
  utilityNode: 60,
};

export const migrateNewToOldFlowStructure = (nodes: Node[], edges: Edge[]): Node[] => {
  const nodeMap = new Map(nodes.map((node) => [node.id, { ...node }]));

  // Create adjacency list to track children
  const childMap = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (!childMap.has(edge.source)) {
      childMap.set(edge.source, []);
    }
    childMap.get(edge.source)!.push(edge.target);
  });

  // Sort nodes by their initial Y position (process top-down)
  const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);

  for (const parentNode of sortedNodes) {
    const children = childMap.get(parentNode.id) || [];
    const parentHeight = OLD_NODE_HEIGHTS[parentNode.type!] || 60;
    let currentY = parentNode.position.y + parentHeight + 50;

    children.forEach((childId) => {
      if (nodeMap.has(childId)) {
        const childNode = nodeMap.get(childId)!;
        const childHeight = OLD_NODE_HEIGHTS[childNode.type!] || 60;

        // Update Y position
        childNode.position.y = currentY;
        childNode.positionAbsolute = { ...childNode.position };

        // Restore old width and height
        childNode.width = 250;
        childNode.height = childHeight;

        // Move down for the next child
        currentY += childHeight + 50;
      }
    });
  }

  return Array.from(nodeMap.values());
};

export const getNodeIdByStepIndex = (edges: Edge[], nodes: Node[], stepIndex: number | undefined) => {
  const steps: { index: number; nodeId: string }[] = [];
  const visitedNodes = new Set();
  let globalIndex = 0;

  const startNode = nodes.find((node) => !edges.some((edge) => edge.target === node.id));
  if (!startNode) return null;

  function traverse(nodeId: string) {
    const currentNodeOuter = nodes.find((node) => node.id === nodeId);

    if (!currentNodeOuter || visitedNodes.has(nodeId)) {
      return;
    }

    visitedNodes.add(nodeId);
    function handleFunctionNode(currentNode: Node, index: number) {
      steps.push({
        index,
        nodeId,
      });

      const connectedEdge = edges.find((edge) => edge.source === currentNode.id);

      if (connectedEdge) {
        traverse(connectedEdge.target);
      }
    }

    function handleConditionalNode(currentNode: Node, index: number) {
      const successEdge = edges.find((edge) => edge.source === currentNode.id && edge.sourceHandle === 'success');
      const failureEdge = edges.find((edge) => edge.source === currentNode.id && edge.sourceHandle === 'failure');

      steps.push({
        index,
        nodeId,
      });

      if (successEdge) {
        traverse(successEdge.target);
      }

      if (failureEdge) {
        traverse(failureEdge.target);
      }
    }

    function handleWaitNode(currentNode: Node, index: number) {
      steps.push({
        index,
        nodeId,
      });

      const connectedEdge = edges.find((edge) => edge.source === currentNode.id);
      if (connectedEdge) {
        traverse(connectedEdge.target);
      }
    }

    function handleUtilityNode(currentNode: Node, index: number) {
      steps.push({
        index,
        nodeId,
      });

      const connectedEdge = edges.find((edge) => edge.source === currentNode.id);

      if (connectedEdge) {
        traverse(connectedEdge.target);
      }
    }

    if (currentNodeOuter.type === NodeVariants.FUNCTION_NODE) {
      handleFunctionNode(currentNodeOuter, globalIndex++);
    } else if (currentNodeOuter.type === NodeVariants.CONDITIONAL_NODE || currentNodeOuter.type === NodeVariants.CONDITION_WITH_DURATION_NODE) {
      handleConditionalNode(currentNodeOuter, globalIndex++);
    } else if (currentNodeOuter.type === NodeVariants.WAIT_NODE) {
      handleWaitNode(currentNodeOuter, globalIndex++);
    } else if (currentNodeOuter.type === NodeVariants.UTILITY_NODE) {
      handleUtilityNode(currentNodeOuter, globalIndex++);
    }
  }

  traverse(startNode.id);

  return steps.find((step) => !_.isNil(stepIndex) && step.index === stepIndex)?.nodeId || null;
};
